import { FC, memo, useCallback, useMemo } from 'react';
import { CallIntelligenceV2 } from '@frontend/api-call-intel';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { useIsCallIntelEligibleAccount } from '@frontend/call-intel-core';
import { PaginatedList } from '@frontend/components';
import { AutoAssignTaskSettingsBanner } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { TaskCenterTabs } from '../constants';
import { useFilterPrivateTasks } from '../hooks';
import { taskCenterTrackingIds } from '../tracking-ids';
import { getTaskEmptyState } from '../utils';
import { TaskItem } from './common';

interface TaskByStatusProps {
  status: TaskCenterTypes.TaskStatus;
}

const EMPTY_TASK_LIST: TaskCenterTypes.Task[] = [];

export const TaskByStatus: FC<TaskByStatusProps> = memo(({ status }) => {
  const { t } = useTranslation('task-center');
  const { setTaskMode, selectedTab, locationIds } = useTaskCenterStore(['setTaskMode', 'selectedTab', 'locationIds']);
  const currentTab = useMemo(() => TaskCenterTabs.find((tab) => tab.controller === selectedTab), [selectedTab]);

  const {
    list,
    hasNextPage,
    status: queryStatus,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useFilterPrivateTasks({
    enabled: currentTab?.status === status,
    status,
  });

  const { isCallIntelActive, activeLocationIds } = useIsCallIntelEligibleAccount(locationIds);

  // Fetch call intel AI task settings and map by location ID
  const { data: callIntelAiTaskSettingMap } = CallIntelligenceV2.Queries.useListAITaskSettingsQuery({
    request: {
      locationIds: activeLocationIds,
    },
    options: {
      enabled: activeLocationIds.length > 0,
      select: (response) => {
        return response.results?.reduce((acc, setting) => {
          acc[setting.locationId] = setting;
          return acc;
        }, {} as Record<string, CallIntelligenceV2.Types.AITaskSetting>);
      },
    },
  });

  const firstCallIntelAutoAssignedTaskId = useMemo(() => {
    if (!isCallIntelActive) {
      return undefined;
    }
    return list?.find(
      (task) =>
        task.source === TaskCenterTypes.TaskSource.SOURCE_CALL_INTELLIGENCE && // ensure the task is from Call Intelligence
        task.createdBy === 'weave-ai' && // ensure the task was created by the AI
        !!task.assignee && // ensure the task has an assignee
        callIntelAiTaskSettingMap?.[task.locationId]?.autoAssignTaskEnabled // check if auto-assign is enabled for the location
    )?.id;
  }, [isCallIntelActive, list, callIntelAiTaskSettingMap]);

  const openCreateTask = useCallback(() => setTaskMode('create'), [setTaskMode]);

  const emptyStateConfig = {
    type: getTaskEmptyState(status),
    ...(status !== TaskCenterTypes.TaskStatus.STATUS_COMPLETED && {
      action: {
        label: t('Create Task'),
        onClick: openCreateTask,
        trackingId: taskCenterTrackingIds.createTaskButton,
        'aria-label': t('Create Task'),
      },
    }),
    header: t('No Tasks Found'),
    description: t('Try adjusting filters, check other tabs, or create a new task to get started.'),
  };

  return (
    <PaginatedList
      listData={list ?? EMPTY_TASK_LIST}
      renderListItem={({ listItem }) => (
        <TaskItem key={listItem.id} {...listItem}>
          {listItem.id === firstCallIntelAutoAssignedTaskId && <AutoAssignTaskSettingsBanner />}
        </TaskItem>
      )}
      hasNextPage={hasNextPage}
      isError={queryStatus === 'error'}
      isLoading={isLoading || isFetching}
      isFetchingNextPage={isFetchingNextPage}
      overscanCount={20}
      loadMore={fetchNextPage}
      emptyStateConfig={emptyStateConfig}
      endOfListText={t('You have reached the end of the list 🎉')}
      height='100%'
    />
  );
});

TaskByStatus.displayName = 'TaskByStatus';
